import type { LayoutPlan, Page, Region, LineBox } from '../models/layout';
import type { LegalBlock, ParagraphBlock, LegalRun } from '../models/ast';
import { getStylesRegistry } from '../styles/registry';
import { layoutParagraph } from './ParagraphLayout';
import { checkSeam, type SeamPolicy } from './rules/widowOrphan';
import { requiresKeepWithNext } from './rules/keepWithNext';
import { resolveNumbering, type NumberingMap } from './NumberingResolver';

export interface PageSetup {
  pageWidthPx: number;
  pageHeightPx: number;
  marginTopPx: number;
  marginRightPx: number;
  marginBottomPx: number;
  marginLeftPx: number;
  headerHeightPx: number;
  footerHeightPx: number;
  widowOrphanDefault?: { minPrev: number; minNext: number };
}

export type PaginationErrorCode =
  | 'BLOCK_EXCEEDS_PAGE_CONTENT_BOX'
  | 'FOOTNOTE_BAND_EXCEEDS_PAGE'
  | 'SEAM_ADJUSTMENT_LIMIT_REACHED';

export class PaginationError extends Error {
  code: PaginationErrorCode;
  details?: Record<string, unknown>;
  constructor(code: PaginationErrorCode, message: string, details?: Record<string, unknown>) {
    super(message);
    this.code = code;
    this.details = details;
    this.name = 'PaginationError';
  }
}

export interface PaginateOptions {
  pageSetup: PageSetup;
}

export async function paginate(ast: LegalBlock[], opts: PaginateOptions): Promise<LayoutPlan> {
  const registry = getStylesRegistry();
  const pages: Page[] = [];
  const selectionForward: Record<string, any> = {};
  const selectionReverse: Record<string, any> = {};
  const pageAnchorCount: Record<number, number> = {};
  const blockFirstPage: Record<string, number> = {};
  const blockPolicy: Record<string, SeamPolicy> = {};
  const numberingMap: NumberingMap = resolveNumbering(ast);

  const contentBox: Region = computeContentBox(opts.pageSetup);
  let pageIndex = 0;
  let cursorY = contentBox.y;
  let currentLines: LineBox[] = [];

  function finalizePage(): void {
    const body: any = { ...contentBox, lineBoxes: currentLines };
    pages.push({
      index: pageIndex,
      header: { x: contentBox.x, y: 0, width: contentBox.width, height: opts.pageSetup.headerHeightPx },
      body,
      footer: { x: contentBox.x, y: opts.pageSetup.pageHeightPx - opts.pageSetup.marginBottomPx - opts.pageSetup.footerHeightPx, width: contentBox.width, height: opts.pageSetup.footerHeightPx, footnotes: [] },
      overflowFlags: [],
    });
    pageIndex += 1;
    cursorY = contentBox.y;
    currentLines = [];
  }

  for (let i = 0; i < ast.length; i++) {
    const block = ast[i];
    // Style resolution (spacing/indents)
    const paragraphStyle = (block as any).styleId ? registry.paragraphStyles[(block as any).styleId] : undefined;
    // Capture per-block widow/orphan policy for later seam reconciliation
    const defaultPolicy: SeamPolicy = opts.pageSetup.widowOrphanDefault ?? { minPrev: 2, minNext: 2 };
    blockPolicy[(block as any).id] = {
      minPrev: paragraphStyle?.widowOrphan?.minPrev ?? defaultPolicy.minPrev,
      minNext: paragraphStyle?.widowOrphan?.minNext ?? defaultPolicy.minNext,
    };
    let spaceBeforePx = paragraphStyle?.spaceBeforePt ? (paragraphStyle.spaceBeforePt * (96 / 72)) : 0;
    const spaceAfterPx = paragraphStyle?.spaceAfterPt ? (paragraphStyle.spaceAfterPt * (96 / 72)) : 0;
    // Refined spacing: suppress spaceBefore at top of a page
    if (cursorY === contentBox.y) spaceBeforePx = 0;

    const isParagraph = block.type === 'Paragraph' || block.type === 'Heading' || block.type === 'Quote' || block.type === 'CodeBlock';
    if (!isParagraph) {
      // Atomic handling in pass 1: if it cannot fit on empty page, throw predictable error
      const atomicHeight = estimateAtomicHeight(block, paragraphStyle);
      if (cursorY + spaceBeforePx + atomicHeight + spaceAfterPx > contentBox.y + contentBox.height) {
        // If page is empty for body area, the block itself exceeds the content box
        if (currentLines.length === 0) {
          throw new PaginationError('BLOCK_EXCEEDS_PAGE_CONTENT_BOX', 'Atomic block exceeds page content area', {
            blockId: (block as any).id,
            pageIndex,
            requiredPx: atomicHeight + spaceBeforePx + spaceAfterPx,
            availablePx: contentBox.height,
          });
        }
        // Otherwise move to next page
        finalizePage();
      }
      // Reserve space and continue (actual drawing handled later)
      cursorY += spaceBeforePx + atomicHeight + spaceAfterPx;
      continue;
    }

    // Paragraph layout
    const res = await layoutParagraph(block as ParagraphBlock, { widthPx: contentBox.width });
    const paraHeight = res.lineBoxes.reduce((h, b) => Math.max(h, b.y + b.height), 0);
    // KeepWithNext lookahead: if current is eligible, ensure first line of next block fits too
    if (requiresKeepWithNext(block) && i + 1 < ast.length) {
      const next = ast[i + 1];
      const nextIsParagraph = next.type === 'Paragraph' || next.type === 'Heading' || next.type === 'Quote' || next.type === 'CodeBlock';
      if (nextIsParagraph) {
        const nextMeasure = await layoutParagraph(next as ParagraphBlock, { widthPx: contentBox.width });
        const firstLineHeight = nextMeasure.lineBoxes[0]?.height ?? Math.max(1, (paragraphStyle?.lineSpacingMultiple ?? 1.5) * (12 * (96 / 72)));
        if (cursorY + spaceBeforePx + paraHeight + spaceAfterPx + firstLineHeight > contentBox.y + contentBox.height) {
          finalizePage();
          // Recompute spaceBefore at page top (suppressed)
          spaceBeforePx = 0;
        }
      }
    }
    // Break if does not fit
    if (cursorY + spaceBeforePx + paraHeight + spaceAfterPx > contentBox.y + contentBox.height) {
      finalizePage();
    }
    // Place paragraph lines with absolute Y offset into the page body region
    const baseY = cursorY + spaceBeforePx;
    for (const lb of res.lineBoxes) {
      currentLines.push({
        ...lb,
        y: lb.y + baseY,
        baseline: lb.baseline + baseY,
      });
    }
    cursorY += spaceBeforePx + paraHeight + spaceAfterPx;
    // Record the first page where this block appears (for cross-refs)
    if (blockFirstPage[(block as any).id] === undefined) {
      blockFirstPage[(block as any).id] = pageIndex;
    }
    // Selection map population (page-local for now; lines are appended in order)
    for (const [k, v] of Object.entries(res.graphemeMap)) {
      const [runId, gStr] = k.split(':');
      const g = Number(gStr);
      const lineBox = currentLines[currentLines.length - res.lineBoxes.length + v.lineIndex];
      if (!lineBox) continue;
      const fwdKey = `${lineBox.runId}:${g}`; // runId:g
      const entry = {
        blockId: (block as any).id,
        runId,
        grapheme: g,
        pageIndex,
        lineIndex: lineBox.lineIndexInBlock,
        x: v.x,
        y: lineBox.baseline,
      } as any;
      selectionForward[fwdKey] = entry;
      const revKey = `${pageIndex}:${lineBox.lineIndexInBlock}:${runId}:${g}`;
      selectionReverse[revKey] = entry;
    }
    // Footnote anchors: count by scanning runs for annotations
    try {
      const runs = (block as any).children as LegalRun[] | undefined;
      const count = Array.isArray(runs) ? runs.reduce((acc, r) => acc + (r.annotations?.footnoteAnchorId ? 1 : 0), 0) : 0;
      pageAnchorCount[pageIndex] = (pageAnchorCount[pageIndex] || 0) + count;
    } catch {}
  }

  // Flush last page
  if (currentLines.length > 0) finalizePage();

  // Pass 2: seam-only reconciliation (basic widow/orphan fix) and simple footnote band sizing with body reclamation.
  for (let pi = 0; pi < pages.length - 1; pi++) {
    const a = pages[pi];
    const b = pages[pi + 1];
    // Determine which paragraph is split at this seam and fetch its policy
    const lastPrev = a.body.lineBoxes[a.body.lineBoxes.length - 1];
    const firstNext = b.body.lineBoxes[0];
    const defaultPolicy: SeamPolicy = opts.pageSetup.widowOrphanDefault ?? { minPrev: 2, minNext: 2 };
    const pid = lastPrev && firstNext && lastPrev.blockId === firstNext.blockId ? lastPrev.blockId : undefined;
    const policy: SeamPolicy = pid ? (blockPolicy[pid] || defaultPolicy) : defaultPolicy;
    const seam = checkSeam(a.body.lineBoxes, b.body.lineBoxes, policy);
    if (!seam.ok) {
      // One correction per seam: move a single line as indicated
      if (seam.pullFromPrev && b.body.lineBoxes.length >= seam.pullFromPrev) {
        const moved = b.body.lineBoxes.splice(0, seam.pullFromPrev);
        a.body.lineBoxes.push(...moved);
      } else if (seam.pushToNext && a.body.lineBoxes.length >= seam.pushToNext) {
        const moved = a.body.lineBoxes.splice(a.body.lineBoxes.length - seam.pushToNext, seam.pushToNext);
        b.body.lineBoxes.unshift(...moved);
      }
    }
  }

  // Footnote reconciliation (v1): per-page band based on anchor count.
  const FOOTNOTE_LINE_PX = 16;
  for (let pi = 0; pi < pages.length; pi++) {
    const p = pages[pi];
    const anchors = pageAnchorCount[pi] || 0;
    const required = anchors * FOOTNOTE_LINE_PX;
    if (required <= 0) {
      p.footer.height = Math.max(0, p.footer.height || 0);
      continue;
    }
    // Grow footer to required height and reduce allowed body height accordingly
    const baseFooter = opts.pageSetup.footerHeightPx;
    p.footer.height = required;
    const allowedBodyHeight = contentBox.height - Math.max(0, required - baseFooter);
    let bodyMax = p.body.lineBoxes.reduce((h, b) => Math.max(h, b.y + b.height), 0);
    while (bodyMax > allowedBodyHeight) {
      // Move one line to next page
      const moved = p.body.lineBoxes.pop();
      if (!moved) break;
      if (!pages[pi + 1]) {
        // create a trailing empty page if needed
        pages.push({
          index: pages.length,
          header: { x: contentBox.x, y: 0, width: contentBox.width, height: opts.pageSetup.headerHeightPx },
          body: { ...contentBox, lineBoxes: [] },
          footer: { x: contentBox.x, y: opts.pageSetup.pageHeightPx - opts.pageSetup.marginBottomPx - opts.pageSetup.footerHeightPx, width: contentBox.width, height: opts.pageSetup.footerHeightPx, footnotes: [] },
          overflowFlags: [],
        });
      }
      pages[pi + 1].body.lineBoxes.unshift(moved);
      bodyMax = p.body.lineBoxes.reduce((h, b) => Math.max(h, b.y + b.height), 0);
      // Prevent runaway loop
      if (p.body.lineBoxes.length === 0) break;
    }
    // If even empty body cannot satisfy required footer band, fail predictably
    const canFit = allowedBodyHeight >= 0;
    if (!canFit && p.body.lineBoxes.length === 0) {
      throw new PaginationError('FOOTNOTE_BAND_EXCEEDS_PAGE', 'Footnote band cannot fit on page', {
        pageIndex: pi,
        requiredPx: required,
        baseFooterPx: baseFooter,
        contentBoxHeight: contentBox.height,
      });
    }
    // Populate footnote boxes (placeholder areas)
    p.footer.footnotes = new Array(anchors).fill(0).map((_, i) => ({
      anchorId: `fn-${pi}-${i}`,
      content: [],
      area: { x: p.footer.x, y: p.footer.y + i * FOOTNOTE_LINE_PX, width: p.footer.width, height: FOOTNOTE_LINE_PX },
    }));
  }

  // Rebuild selection maps after seam/footnote adjustments
  const rebuilt = rebuildSelectionMaps(pages, selectionForward);
  const newForward = rebuilt.forward;
  const newReverse = rebuilt.reverse;

  // Build blockId -> first page index (1-based) for cross-ref resolution
  const blockToPage: Record<string, number> = {};
  for (let pi = 0; pi < pages.length; pi++) {
    const lines = pages[pi].body.lineBoxes;
    for (const lb of lines) {
      if (blockToPage[lb.blockId] === undefined) blockToPage[lb.blockId] = pi + 1;
    }
  }

  // Cross-reference resolution with unresolved handling
  const crossRefs: Array<{ sourceRunId: string; targetBlockId: string; targetPage?: number; display?: string; status: 'resolved' | 'unresolved'; reason?: string }> = [];
  for (const block of ast) {
    const runs = (block as any).children as LegalRun[] | undefined;
    if (!Array.isArray(runs)) continue;
    for (const run of runs) {
      const cf = run.annotations?.crossRef;
      if (!cf) continue;
      const sourceRunId = run.id;
      const targetBlockId = cf.targetBlockId;
      const page = blockToPage[targetBlockId];
      if (page === undefined) {
        crossRefs.push({ sourceRunId, targetBlockId, status: 'unresolved', reason: 'missing_target' });
      } else {
        const numberEntry = numberingMap[targetBlockId];
        const numberText = numberEntry?.displayNumber || '';
        const display = (cf.format || '{page}')
          .replace('{page}', String(page))
          .replace('{number}', numberText);
        crossRefs.push({ sourceRunId, targetBlockId, targetPage: page, display, status: 'resolved' });
      }
    }
  }

  const planHash = hashPlan(pages);
  // attach per-page hash too
  pages.forEach((p) => (p.pageHash = hashPage(p)));
  return {
    pages,
    selectionMap: { forward: newForward, reverse: newReverse },
    planHash,
    crossRefs,
  };
}

function computeContentBox(setup: PageSetup): Region {
  const x = setup.marginLeftPx;
  const y = setup.marginTopPx + setup.headerHeightPx;
  const width = setup.pageWidthPx - setup.marginLeftPx - setup.marginRightPx;
  const height = setup.pageHeightPx - setup.marginTopPx - setup.marginBottomPx - setup.headerHeightPx - setup.footerHeightPx;
  return { x, y, width, height };
}

function estimateAtomicHeight(_block: LegalBlock, paragraphStyle: any): number {
  // Placeholder: in future derive from block attrs (e.g., image height). For now use style spacing only.
  const lineHeightMultiple = paragraphStyle?.lineSpacingMultiple ?? 1.5;
  const fontSizePx = ((paragraphStyle?.defaultCharacterStyleId && 12) || 12) * (96 / 72);
  return Math.max(24, Math.round(lineHeightMultiple * fontSizePx));
}

function hashPage(p: Page): string {
  const parts: string[] = [];
  parts.push(String(p.index));
  p.body?.lineBoxes?.forEach((b) => parts.push(`${b.x}|${b.y}|${b.width}|${b.height}|${b.startGrapheme}|${b.endGrapheme}`));
  return simpleHash(parts.join(';'));
}

function hashPlan(pages: Page[]): string {
  return simpleHash(pages.map((p) => hashPage(p)).join('::'));
}

function simpleHash(input: string): string {
  let h = 2166136261 >>> 0; // FNV-1a basis
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return ('00000000' + h.toString(16)).slice(-8);
}

function rebuildSelectionMaps(pages: Page[], prevForward: Record<string, any>): { forward: Record<string, any>; reverse: Record<string, any> } {
  const forward: Record<string, any> = {};
  const reverse: Record<string, any> = {};
  for (let pi = 0; pi < pages.length; pi++) {
    const body = pages[pi].body;
    for (let li = 0; li < body.lineBoxes.length; li++) {
      const b = body.lineBoxes[li];
      for (let g = b.startGrapheme; g <= b.endGrapheme; g++) {
        const key = `${b.runId}:${g}`;
        const prev = prevForward[key];
        const entry = {
          blockId: b.blockId,
          runId: b.runId,
          grapheme: g,
          pageIndex: pi,
          lineIndex: b.lineIndexInBlock,
          x: prev?.x ?? b.x,
          y: b.baseline,
        } as any;
        forward[key] = entry;
        reverse[`${pi}:${b.lineIndexInBlock}:${b.runId}:${g}`] = entry;
      }
    }
  }
  return { forward, reverse };
}


